import React, { useState, useEffect } from "react";
import { Typography, TextField, Button, FormError } from "components";
import queryString from "query-string";
import { Formik, Form, ErrorMessage } from "formik";
import { BsFillCheckCircleFill } from "react-icons/bs";
import { passwordValidation } from "utils/validators";
import { routes } from "routes/apiRoutes";
import { RegistrationModal } from "components";
import * as Yup from "yup";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { ROUTES, pocketMoniUrl } from "routes/routePaths";
import AuthHeader from "./AuthHeader";
import AuthFooter from "./AuthFooter";
import resetImage from "assets/images/reset-image.png";
import pmLogo from "assets/images/pocketmoni-logo.png";
import { HiOutlineLockClosed } from "react-icons/hi";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

import "./style.scss";
import makeRequest from "services/makeRequest";
import { isSuccessResponse, setAlert } from "utils/helper";

const PasswordSet = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [passwordType, setPasswordType] = useState(true);
  const [confirmPasswordType, setConfirmPasswordType] = useState(true);
  const [otpVerified, setOtpVerified] = useState(false);
  const [state, setState] = useState({
    showError: false,
    showModal: false,
  });

  const reset = location?.state?.reset;
  const passKey = location?.state?.passKey;

  const { token } = queryString.parse(location.search);

  const handlePasswordType = () => setPasswordType(!passwordType);

  const handleConfirmPasswordType = () =>
    setConfirmPasswordType(!confirmPasswordType);

  const passwordRules = [
    "Minimum of twelve(12) characters",
    "At least one special character and number",
    "At least one uppercase and lowercase character",
    `Password can't be your first, last name, username ${
      reset ? "or previously used passwords." : ""
    }`,
  ];

  const showOtpVerified = () => {
    setOtpVerified(true);
    setInterval(() => {
      setOtpVerified(false);
    }, 2000);
  };

  useEffect(() => {
    reset && showOtpVerified();
  }, [reset]);

  const validationSchema = () =>
    Yup.object({
      password: passwordValidation(),
      confirmPassword: Yup.string()
        .required("Re-enter Password")
        .oneOf([Yup.ref("password"), null], "Passwords do not match"),
    });

  const handlePasswordSet = async (values, resetForm) => {
    let tokenToSend = reset ? passKey : token;
    try {
      const { data } = await makeRequest(
        "post",
        routes.setPassword(tokenToSend),
        values
      );
      if (isSuccessResponse(data)) {
        if (reset) {
          setState({
            showError: false,
            showModal: true,
          });
          resetForm();
        } else {
          navigate(ROUTES.passwordSetSuccess);
        }
      } else {
        setAlert(data.responseMessage);
      }
    } catch (err) {}

    return;
  };

  if (!location?.state?.passKey && !token)
    return <Navigate to={ROUTES.notFound} />;

  return (
    <div className="row m-0 auth-container">
      <div className="auth-header-container">
        <AuthHeader />
      </div>

      <div className="col-12 col-xl-6 p-0 auth-form-container">
        <div className="login-form">
          <div className="auth-intro">
            <div className="mb-4 d-xl-none">
              <a href={pocketMoniUrl} target="_blank" rel="noopener noreferrer">
                <img src={pmLogo} width="150" alt="PocketMoni" />
              </a>{" "}
            </div>
            <Typography component="h3" variant="heading3" state="primary">
              {reset ? "Reset" : "Set"} your Password
            </Typography>
            <Typography variant="body3" state="primary">
              You can now {reset ? "Reset your" : "Set your"} password here
            </Typography>
          </div>
          {otpVerified && (
            <div className="text-center mt-3">
              <BsFillCheckCircleFill color="#27AE60" />{" "}
              <Typography variant="body2" state="success" component="span">
                OTP was verified!
              </Typography>
            </div>
          )}

          <Formik
            initialValues={{
              password: "",
              confirmPassword: "",
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { resetForm }) =>
              handlePasswordSet(values, resetForm)
            }
          >
            {({ errors, touched, getFieldProps, isSubmitting }) => (
              <Form noValidate autoComplete="off">
                <div className="mb-4">
                  {state.showError && (
                    <Typography variant="body3" state="danger">
                      Password cannot be first, last name or username
                    </Typography>
                  )}
                  <TextField
                    name="password"
                    type={passwordType ? "password" : "text"}
                    placeholder="Enter Password"
                    label="Password"
                    {...getFieldProps("password")}
                    hasError={errors.password && touched.password}
                    startIcon={
                      <HiOutlineLockClosed color="#AAA6A6" fontSize="19px" />
                    }
                    endIcon={
                      <div
                        style={{ cursor: "pointer" }}
                        onClick={handlePasswordType}
                      >
                        {passwordType ? (
                          <AiOutlineEye />
                        ) : (
                          <AiOutlineEyeInvisible />
                        )}
                      </div>
                    }
                  />
                  <ErrorMessage component={FormError} name="password" />
                </div>

                <div className="mb-4">
                  <TextField
                    name="confirmPassword"
                    type={confirmPasswordType ? "password" : "text"}
                    placeholder="Enter Password"
                    label="Confirm Password"
                    {...getFieldProps("confirmPassword")}
                    hasError={errors.confirmPassword && touched.confirmPassword}
                    startIcon={
                      <HiOutlineLockClosed color="#AAA6A6" fontSize="19px" />
                    }
                    endIcon={
                      <div
                        style={{ cursor: "pointer" }}
                        onClick={handleConfirmPasswordType}
                      >
                        {confirmPasswordType ? (
                          <AiOutlineEye />
                        ) : (
                          <AiOutlineEyeInvisible />
                        )}
                      </div>
                    }
                  />
                  <ErrorMessage component={FormError} name="confirmPassword" />
                </div>

                <Button
                  type="submit"
                  label={`${reset ? "Reset" : "Set"} Password`}
                  fullWidth
                  loading={isSubmitting}
                  style={{ marginTop: "3rem" }}
                />
              </Form>
            )}
          </Formik>
          <div className="mt-4 password-rules">
            <Typography variant="body4" state="primary">
              Ensure that your password meets the following Requirements
            </Typography>
            <ul>
              {passwordRules.map((passwordRule, key) => (
                <li key={key}>
                  <Typography variant="caption1">{passwordRule}</Typography>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <RegistrationModal
          onClick={() => navigate(ROUTES.login)}
          modalContentMapper="registrationComplete"
          bodyText={
            "Your password has been successfully changed. You can login to your account here."
          }
          headerText="Password Reset Successful!"
          showModal={state.showModal}
          onClose={() => navigate(ROUTES.login)}
        />
        <AuthFooter />
      </div>

      <div className="d-none d-xl-block col-xl-6 p-0">
        <img src={resetImage} width="100%" alt="Log In" />
      </div>
    </div>
  );
};

export default PasswordSet;
